// ui/presenters/fundingSourceVersionPresenter.js
import { i18n } from 'boot/i18n';

import { formatCalendarDate } from 'src/utils/DateUtils';

import {
  canTakeEffectAt,
  currentAsOf,
  minEffectiveFrom,
  scheduledAsOf,
  supersededBySaveAt,
} from '../../domain/model/VersionTimeline';
import { toAsOfDate } from '../../domain/services/effectiveDating';

/**
 * Presenter: turns a funder's version timeline into what the editor's dialog renders.
 *
 * In `presenters/` because it holds no state and no reactivity — a pure mapping from the timeline to
 * a view model, which is the Presenter role in the interface-adapters ring. The dialog then renders
 * props and emits a choice, and the domain stays free of dialog vocabulary.
 *
 * THIS IS WHERE THE THREE SAVE OPTIONS LIVE, and they live here rather than in the domain on purpose.
 * "Correct the current version", "today" and "on a specific date" are three ways to pick ONE date —
 * the write is identical in all three cases, so they are affordances rather than modes. The domain
 * therefore knows only dates (see `canTakeEffectAt`), and a fourth affordance would be a change to
 * this file alone.
 *
 * THE TODAY OPTION IS WITHHELD when the version being edited already starts today, because then it
 * and "correct the current version" would write the same thing. Offering both is offering a choice
 * that is not one. This case did not exist while the option was "tomorrow" — tomorrow is always a
 * date nothing occupies — so it arrived with the decision to make the shortcut immediate.
 */

/**
 * The three ways the editor offers to date a save. UI affordances, not domain concepts.
 *
 * @typedef {'amend' | 'today' | 'on_date'} FundingSourceSaveChoice
 */
export const SAVE_CHOICE = {
  AMEND: 'amend',
  TODAY: 'today',
  ON_DATE: 'on_date',
};

/**
 * Where the version-dialog strings live for the record being edited.
 *
 * PARAMETERISED RATHER THAN DUPLICATED. Every rule in this file is a function of the timeline alone —
 * which dates a save may take, what a correction supersedes, how far back the picker may go — so the
 * only thing that is actually funder-specific is the i18n namespace the captions come from. Charge
 * masters version identically and ask the user the same question, and a second copy of these rules
 * would be the kind of duplicate that drifts silently: the two would go on agreeing right up until
 * somebody fixed a date edge case in one of them.
 *
 * Callers pass their own prefix; the funder's is the default so its call sites did not have to move.
 * A namespace must define every name used below.
 */
const FUNDING_SOURCE_KEYS = 'settings.fundingSources.versions';

const keyIn = (keyPrefix, name) => `${keyPrefix}.${name}`;

/**
 * The as-of these presenters run against, or a refusal.
 *
 * REFUSED RATHER THAN RENDERED AROUND. A presenter is beneath the edge, so it receives the session
 * date and never invents one. Without this the comparisons below simply read false, the today option
 * is offered anyway, its label renders whatever `formatCalendarDate` makes of a non-date, and
 * `effectiveDateFor` hands that same value back as the date to save under. The domain refuses such a
 * value, so nothing corrupt is written — but the user picks an option labeled with nonsense first,
 * and the failure surfaces at the save rather than at the mistake.
 */
const requireAsOf = (asOf, presenter) => {
  const asOfDate = toAsOfDate(asOf);

  if (asOfDate === null) {
    throw new TypeError(`${presenter} needs a 'YYYY-MM-DD' as-of date; got ${JSON.stringify(asOf)}.`);
  }
  return asOfDate;
};

/**
 * The version line above the form: which version is in view, and which others can be switched to.
 *
 * The version marker is withheld at v1 — "v1" tells a user nothing they did not already assume, and
 * showing it on every funder would make the marker meaningless where it matters.
 *
 * Switching is OFFERED here and carried out by the caller, because it reloads the form. Only the
 * version in force and any scheduled ones are listed; past versions are not reachable yet.
 *
 * @param {import('../../domain/model/VersionTimeline').VersionTimeline} timeline
 * @param {Object} context
 * @param {string} context.asOf - 'YYYY-MM-DD'
 * @param {import('../../domain/model/VersionTimeline').ConfigVersion|null} context.selected - the
 *   version being edited
 * @returns {{ effectiveFromLabel: string|null, versionLabel: string|null,
 *   versionOptions: Array<{ value: string, label: string }> }}
 */
export function presentVersionLine(timeline, { asOf, selected, keyPrefix = FUNDING_SOURCE_KEYS }) {
  requireAsOf(asOf, 'presentVersionLine');

  if (selected === null) {
    return { effectiveFromLabel: null, versionLabel: null, versionOptions: [] };
  }

  const current = currentAsOf(timeline, asOf);
  const switchable = [current, ...scheduledAsOf(timeline, asOf)]
    .filter((version) => version !== null && version.versionId !== selected.versionId)
    .map((version) => ({
      value: version.versionId,
      label: i18n.t(keyIn(keyPrefix, version.effectiveFrom > asOf ? 'switchToScheduled' : 'switchToCurrent'), {
        date: formatCalendarDate(version.effectiveFrom),
      }),
    }));

  return {
    effectiveFromLabel: i18n.t(keyIn(keyPrefix, 'validFrom'), {
      date: formatCalendarDate(selected.effectiveFrom),
    }),
    versionLabel: selected.version > 1
      ? i18n.t(keyIn(keyPrefix, 'versionMarker'), { version: selected.version })
      : null,
    versionOptions: switchable,
  };
} // presentVersionLine()

/**
 * The dated-save options, in the order the design presents them.
 *
 * Captions name the consequence rather than the mechanism — what happens to the period, not what
 * happens to the document — because the specialist is deciding about coverage in time, not about
 * storage.
 *
 * @param {import('../../domain/model/VersionTimeline').VersionTimeline} timeline
 * @param {Object} context
 * @param {string} context.asOf - 'YYYY-MM-DD'
 * @param {import('../../domain/model/VersionTimeline').ConfigVersion|null} context.selected
 * @returns {Array<{ value: FundingSourceSaveChoice, label: string, caption: string,
 *   withDatePicker?: boolean }>}
 */
export function presentSaveChoices(timeline, { asOf, selected, keyPrefix = FUNDING_SOURCE_KEYS }) {
  requireAsOf(asOf, 'presentSaveChoices');

  // A funder being CREATED still gets asked, just with one fewer option. There is nothing to correct,
  // so the amend option is absent — but "from today" and "from a date" are both real answers, and a
  // payer whose terms started in January is an ordinary thing to be entering now. What it does not
  // get is a floor: with no version in force there is nothing for a new one to sit after.
  if (selected === null) {
    return [
      {
        value: SAVE_CHOICE.TODAY,
        label: i18n.t(keyIn(keyPrefix, 'todayLabel'), { date: formatCalendarDate(asOf) }),
        caption: i18n.t(keyIn(keyPrefix, 'todayCaptionNew')),
      },
      {
        value: SAVE_CHOICE.ON_DATE,
        label: i18n.t(keyIn(keyPrefix, 'onDateLabel')),
        caption: i18n.t(keyIn(keyPrefix, 'onDateCaptionNew')),
        withDatePicker: true,
      },
    ];
  }

  const isScheduled = selected.effectiveFrom > asOf;
  const choices = [{
    value: SAVE_CHOICE.AMEND,
    label: i18n.t(keyIn(keyPrefix, isScheduled ? 'amendScheduledLabel' : 'amendCurrentLabel')),
    caption: i18n.t(keyIn(keyPrefix, isScheduled ? 'amendScheduledCaption' : 'amendCurrentCaption'), {
      date: formatCalendarDate(selected.effectiveFrom),
    }),
  }];

  // Withheld when it would duplicate the option above — see the note at the top of this file.
  if (selected.effectiveFrom !== asOf && canTakeEffectAt(timeline, asOf, asOf)) {
    choices.push({
      value: SAVE_CHOICE.TODAY,
      label: i18n.t(keyIn(keyPrefix, 'todayLabel'), { date: formatCalendarDate(asOf) }),
      caption: i18n.t(keyIn(keyPrefix, 'todayCaption')),
    });
  }

  choices.push({
    value: SAVE_CHOICE.ON_DATE,
    label: i18n.t(keyIn(keyPrefix, 'onDateLabel')),
    caption: i18n.t(keyIn(keyPrefix, 'onDateCaption')),
    withDatePicker: true,
  });

  return choices;
} // presentSaveChoices()

/**
 * The date a choice resolves to, or null when the choice is incomplete.
 *
 * The whole reduction from three options to one write, in one function. `pickedDate` is only
 * consulted for the option that offers a calendar, so a stale date left behind by switching options
 * cannot leak into a save.
 *
 * @param {FundingSourceSaveChoice|null} choice
 * @param {Object} context
 * @param {string} context.asOf - 'YYYY-MM-DD'
 * @param {import('../../domain/model/VersionTimeline').ConfigVersion|null} context.selected
 * @param {string|null} context.pickedDate - 'YYYY-MM-DD'
 * @returns {string|null} 'YYYY-MM-DD'
 */
export function effectiveDateFor(choice, { asOf, selected, pickedDate }) {
  requireAsOf(asOf, 'effectiveDateFor');

  switch (choice) {
    case SAVE_CHOICE.AMEND:
      return selected?.effectiveFrom ?? null;
    case SAVE_CHOICE.TODAY:
      return asOf;
    case SAVE_CHOICE.ON_DATE:
      return pickedDate;
    default:
      // No choice made yet, or one this presenter does not offer. Null rather than a sensible-looking
      // default, because a date nobody picked is exactly what the confirm button's disabled state
      // exists to prevent from being written — and that applies to a funder being created too, which
      // is now asked the same question.
      return null;
  }
} // effectiveDateFor()

/**
 * The earliest date the calendar may offer, or null for no floor.
 *
 * @param {import('../../domain/model/VersionTimeline').VersionTimeline} timeline
 * @param {string} asOf - 'YYYY-MM-DD'
 * @returns {string|null}
 */
export function presentMinDate(timeline, asOf) {
  requireAsOf(asOf, 'presentMinDate');

  return minEffectiveFrom(timeline, asOf);
} // presentMinDate()

/**
 * The latest day the calendar may offer, or null when there is no ceiling.
 *
 * ONLY WHEN CREATING, which is what `selected === null` means here, the same way `presentSaveChoices`
 * reads it. Once a series exists, a version may always be scheduled forward: that is the timeline's
 * whole purpose, so there is no ceiling to impose.
 *
 * THE CEILING ITSELF IS THE CALLER'S, and it is passed rather than computed here for the same reason
 * `keyPrefix` is. Whether a record may START in the future is the entity's own policy, not a property
 * of versioning: a funding source may not, because a payer that applies from a later date applies on
 * no date at all and is reachable through nothing (see `fundingSourceDating`), while a fee schedule
 * beginning on 1 January is an ordinary thing to enter in December. Defaulting this to the funder's
 * answer would hand it silently to every other record that versions the same way — which is exactly
 * how the two would go on agreeing until somebody changed one of them.
 *
 * @param {Object} context
 * @param {string} context.asOf - 'YYYY-MM-DD'
 * @param {import('../../domain/model/VersionTimeline').ConfigVersion|null} context.selected
 * @param {string|null} [context.latestFirstStart] - 'YYYY-MM-DD', the last day this record's FIRST
 *   version may take. Null, the default, means the record permits a future start; a caller whose write
 *   path refuses one must pass the same value that path reads, or a greyed-out day and a refused save
 *   disagree.
 * @returns {string|null} 'YYYY-MM-DD'
 */
export function presentMaxDate({ asOf, selected, latestFirstStart = null }) {
  requireAsOf(asOf, 'presentMaxDate');

  return selected === null ? latestFirstStart : null;
} // presentMaxDate()

/**
 * The warning shown before a save that cancels a scheduled change, or null when none would be.
 *
 * Worth a sentence rather than a disabled option: replacing a scheduled change is a legitimate thing
 * to want, and the user cannot be expected to remember one a colleague queued weeks ago.
 *
 * @param {import('../../domain/model/VersionTimeline').VersionTimeline} timeline
 * @param {Object} context
 * @param {string} context.asOf - 'YYYY-MM-DD'
 * @param {string|null} context.effectiveFrom - 'YYYY-MM-DD' the date being saved to
 * @returns {string|null}
 */
export function presentReplacementWarning(timeline, { asOf, effectiveFrom, keyPrefix = FUNDING_SOURCE_KEYS }) {
  requireAsOf(asOf, 'presentReplacementWarning');

  if (effectiveFrom === null) {
    return null;
  }
  const superseded = supersededBySaveAt(timeline, asOf, effectiveFrom);

  if (superseded.length === 0) {
    return null;
  }

  // The soonest one is named. More than one can only exist through a concurrent save, and a warning
  // that enumerates an anomaly reads as a bug report rather than a caution.
  return i18n.t(keyIn(keyPrefix, 'replacesScheduled'), {
    date: formatCalendarDate(superseded[0].effectiveFrom),
  });
} // presentReplacementWarning()
